import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import Pagination from '../components/Pagination';
import './Dashboard.css';

const Zonas = () => {
  const { user } = useContext(AuthContext);
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingZona, setEditingZona] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'activo'
  });

  // Paginación y Filtros
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchZonas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/zonas');
      setZonas(res.data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error al cargar zonas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchZonas();
    }
  }, [fetchZonas, user]);

  const handleOpenCreateModal = () => {
    setEditingZona(null);
    setFormData({
      name: '',
      description: '',
      status: 'activo'
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (zona) => {
    setEditingZona(zona);
    setFormData({
      name: zona.name,
      description: zona.description || '',
      status: zona.status || 'activo'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      if (editingZona) {
        await axios.put(`/api/zonas/${editingZona.id}`, formData);
        setSuccess('Zona actualizada correctamente');
      } else {
        await axios.post('/api/zonas', formData);
        setSuccess('Zona creada correctamente');
      }
      setShowModal(false);
      fetchZonas();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar la zona');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta zona? Los puntos de venta y tránsitos quedarán huérfanos de zona.')) return;
    try {
      setError('');
      setSuccess('');
      await axios.delete(`/api/zonas/${id}`);
      setSuccess('Zona eliminada con éxito');
      fetchZonas();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar la zona');
    }
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Navbar title="🗺️ Gestión de Zonas" />
          <div className="page-content">
            <div className="error-banner">No tienes permisos para acceder a esta sección. Solo TransiApp puede administrar el catálogo global de zonas.</div>
          </div>
        </div>
      </div>
    );
  }

  // Filtrado
  const filteredZonas = zonas.filter(z => 
    z.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (z.description && z.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredZonas.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Navbar title="🗺️ Catálogo Global de Zonas" />
        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}
          {success && <div className="success-banner" style={{ padding: '12px', marginBottom: '20px', background: '#ecfdf5', color: '#065f46', borderRadius: '8px', border: '1px solid #a7f3d0' }}>{success}</div>}

          <div className="page-header">
            <div className="header-title">
              <h2>Catálogo Global de Zonas</h2>
              <p className="subtitle">Administra las zonas maestras para agrupar puntos de venta y tránsitos</p>
            </div>
            <button onClick={handleOpenCreateModal} className="btn-primary">
              ➕ Crear Zona
            </button>
          </div>

          <div className="dashboard-controls" style={{ marginBottom: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <div className="search-box">
              <input 
                type="text" 
                placeholder="🔍 Buscar por nombre o descripción..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="form-input"
                style={{ width: '300px' }}
              />
            </div>
          </div>

          {loading ? (
            <p className="loading">Cargando zonas...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nombre de la Zona</th>
                      <th>Descripción</th>
                      <th>Puntos de Venta</th>
                      <th>Tránsitos</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map(z => (
                      <tr key={z.id}>
                        <td><strong>#{z.id}</strong></td>
                        <td><strong style={{ color: '#1e293b' }}>📍 {z.name}</strong></td>
                        <td>{z.description || '-'}</td>
                        <td>
                          <span className="profile-badge" style={{ display: 'inline-block', minWidth: '24px', textAlign: 'center' }}>
                            🏪 {z.puntos_venta_count || 0}
                          </span>
                        </td>
                        <td>
                          <span className="profile-badge" style={{ display: 'inline-block', minWidth: '24px', textAlign: 'center', background: '#e0e7ff', color: '#3730a3' }}>
                            🏢 {z.transitos_count || 0}
                          </span>
                        </td>
                        <td>
                          <span className={`status ${z.status || 'activo'}`}>
                            {z.status || 'activo'}
                          </span>
                        </td>
                        <td className="actions">
                          <button onClick={() => handleOpenEditModal(z)} className="btn-action btn-edit" title="Editar">
                            ✏️
                          </button>
                          <button onClick={() => handleDelete(z.id)} className="btn-action btn-delete" title="Eliminar">
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredZonas.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No se encontraron zonas registradas.
                </div>
              )}

              <Pagination 
                currentPage={currentPage}
                totalItems={filteredZonas.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
                onItemsPerPageChange={(items) => { setItemsPerPage(items); setCurrentPage(1); }}
              />
            </div>
          )}
        </div>
      </div>

      {/* MODAL CREAR / EDITAR ZONA */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <h3>{editingZona ? '✏️ Editar Zona' : '➕ Crear Zona'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre de la Zona *</label>
                <input 
                  type="text"
                  placeholder="Ej: Zona Norte, Zona Centro, Magdalena..."
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Descripción (Opcional)</label>
                <textarea 
                  placeholder="Detalles u observaciones de la zona..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input"
                  rows="3"
                />
              </div>

              {editingZona && (
                <div className="form-group">
                  <label>Estado</label>
                  <select 
                    value={formData.status} 
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="form-input"
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingZona ? 'Guardar Cambios' : 'Crear Zona'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Zonas;
